import { useQuery } from '@tanstack/react-query';
import { Button } from '@mailflow/ui';
import { checkSpamScore } from '@mailflow/shared';
import { versandVorschau, type SendingPolicy as PolicyRegeln } from '@mailflow/core/sending';
import { api } from '../lib/api.js';
import { describeWindows, testSendsQuery, type SendingPolicy } from '../lib/campaigns.js';

/**
 * Der Startknopf startet nicht sofort.
 *
 * Er öffnet diese Bestätigung mit den ECHTEN Zahlen und einer Prüfliste. Der
 * Unterschied zwischen „Senden“ und „4.453 E-Mails senden“ ist genau der
 * Moment, in dem jemand noch einmal nachdenkt.
 *
 * ZUR EHRLICHKEIT DER PRÜFLISTE: hier steht nur, was tatsächlich geprüft
 * wurde. Ein Häkchen bei „SPF, DKIM, DMARC“, das niemand nachgesehen hat,
 * wäre eine erfundene Angabe — und zwar die gefährlichste Sorte, weil sie
 * genau dort Sicherheit vortäuscht, wo es teuer wird. Was diese Seite nicht
 * wissen kann, steht als Hinweis mit Verweis auf das Zustellbarkeits-Center.
 */

interface Sender {
  id: string;
  name: string;
  fromEmail: string;
  status: string;
  pausedAt: string | null;
  lastVerifiedAt: string | null;
}

type Schwere = 'blockiert' | 'warnt' | 'ok';

interface Pruefpunkt {
  schwere: Schwere;
  text: string;
}

export interface StartDialogProps {
  campaignId: string | null;
  kampagnenname: string;
  betreff: string;
  html: string;
  empfaenger: number | null;
  profil: SendingPolicy | undefined;
  onAbbrechen: () => void;
  onSenden: () => void;
  laeuft: boolean;
}

const datumFormat = new Intl.DateTimeFormat('de-DE', {
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

const ZEICHEN = { blockiert: '✕', warnt: '⚠', ok: '✓' } as const;

export function StartDialog({
  campaignId,
  kampagnenname,
  betreff,
  html,
  empfaenger,
  profil,
  onAbbrechen,
  onSenden,
  laeuft,
}: StartDialogProps) {
  const absender = useQuery({
    queryKey: ['senders'],
    queryFn: ({ signal }) => api.get<{ items: Sender[] }>('/api/senders', signal),
  });

  const tests = useQuery({
    ...testSendsQuery(campaignId ?? ''),
    enabled: Boolean(campaignId),
  });

  const spam = checkSpamScore(betreff, html);
  const verwendbar = (absender.data?.items ?? []).filter(
    (s) => s.status === 'active' && s.pausedAt === null,
  );
  const letzterTest = tests.data?.items.find((t) => t.ok) ?? null;

  const punkte: Pruefpunkt[] = [];

  // --- Blockierend ---------------------------------------------------------

  if (absender.isLoading) {
    punkte.push({ schwere: 'warnt', text: 'Absender werden noch geladen …' });
  } else if (verwendbar.length === 0) {
    punkte.push({
      schwere: 'blockiert',
      text: 'Kein verwendbarer Absender. Ohne ihn verschickt der Worker nichts — richte unter Zustellbarkeit einen ein.',
    });
  } else {
    // Der Worker nimmt den Absender aus dem Pool, nicht die im Formular
    // eingetragene Adresse. Das gehört hier hin, sonst wundert sich jemand
    // über den Absender der angekommenen Mail.
    punkte.push({
      schwere: 'ok',
      text: `Versendet über „${verwendbar[0]!.name}“ (${verwendbar[0]!.fromEmail})${
        verwendbar.length > 1 ? ` und ${verwendbar.length - 1} weitere im Wechsel` : ''
      }`,
    });
  }

  if (empfaenger === null) {
    punkte.push({
      schwere: 'blockiert',
      text: 'Die Empfängerzahl ist unbekannt — die Abfrage ist nicht durchgekommen.',
    });
  } else if (empfaenger === 0) {
    punkte.push({ schwere: 'blockiert', text: 'Keine Empfänger. Es gibt nichts zu senden.' });
  } else {
    punkte.push({
      schwere: 'ok',
      text: `${empfaenger.toLocaleString('de-DE')} Empfänger nach Abzug von Ausschluss und Sperrliste`,
    });
  }

  if (betreff.trim().length === 0) {
    punkte.push({ schwere: 'blockiert', text: 'Kein Betreff.' });
  }

  if (profil && profil.sendWindows.length === 0) {
    punkte.push({
      schwere: 'blockiert',
      text: `Sendeprofil „${profil.name}“ hat kein Sendefenster — mit ihm geht nie etwas raus.`,
    });
  } else if (profil) {
    punkte.push({ schwere: 'ok', text: `Sendeprofil: ${describeWindows(profil.sendWindows)}` });
  } else {
    punkte.push({
      schwere: 'warnt',
      text: 'Kein Sendeprofil gewählt — es wird ohne Warmup, ohne Limit und ohne Sendefenster versendet.',
    });
  }

  // --- Warnend -------------------------------------------------------------

  if (campaignId && letzterTest) {
    punkte.push({
      schwere: 'ok',
      text: `Testversand durchgeführt (${new Intl.DateTimeFormat('de-DE', {
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(letzterTest.createdAt))} an ${letzterTest.recipients.length} ${
        letzterTest.recipients.length === 1 ? 'Adresse' : 'Adressen'
      })`,
    });
  } else {
    punkte.push({
      schwere: 'warnt',
      text: 'Kein Testversand. Ein Tippfehler im Betreff geht sonst an alle.',
    });
  }

  if (spam.rating === 'kritisch' || spam.rating === 'mittel') {
    const staerkste = [...spam.issues]
      .sort((a, b) => b.penalty - a.penalty)
      .slice(0, 3)
      .map((i) => i.message);
    punkte.push({
      schwere: spam.rating === 'kritisch' ? 'blockiert' : 'warnt',
      text: `Spam-Bewertung ${spam.score} von 100 (${spam.rating})${
        staerkste.length > 0 ? ` — ${staerkste.join('; ')}` : ''
      }`,
    });
  } else {
    punkte.push({ schwere: 'ok', text: `Spam-Bewertung ${spam.score} von 100 (${spam.rating})` });
  }

  /*
   * Der Abmeldelink wird beim Versand serverseitig ergänzt, wenn er fehlt
   * (ensureUnsubscribeFooter). Deshalb steht hier kein „fehlt“ — das wäre
   * falsch. Wohl aber der Hinweis, dass er dann automatisch unten landet und
   * nicht dort, wo man ihn haben will.
   */
  if (!/\{\{\s*unsubscribe_url\s*\}\}|\/api\/unsubscribe\//.test(html)) {
    punkte.push({
      schwere: 'warnt',
      text: 'Kein eigener Abmeldelink im Text — es wird automatisch einer unten angehängt.',
    });
  }

  const vorschau = profil && empfaenger ? rechne(profil, empfaenger) : null;
  if (vorschau && vorschau.dauerTage > 14) {
    punkte.push({
      schwere: 'warnt',
      text: `Der Versand zieht sich über ${vorschau.dauerTage} Tage. Bei kaltem Anschreiben ist die erste Mail vergessen, wenn die letzte rausgeht.`,
    });
  }

  const blockiert = punkte.some((p) => p.schwere === 'blockiert');

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onAbbrechen()}>
      <div className="card dialog" role="dialog" aria-modal="true" aria-label="Kampagne senden">
        <div className="card__body stack">
          <h2 className="text-lg">
            Kampagne „{kampagnenname || 'Ohne Namen'}“
            {empfaenger ? ` an ${empfaenger.toLocaleString('de-DE')} Empfänger` : ''} senden?
          </h2>

          <ul className="pruefliste">
            {punkte.map((p, i) => (
              <li key={i} className="pruefliste__punkt" data-schwere={p.schwere}>
                <span aria-hidden="true">{ZEICHEN[p.schwere]}</span>
                <span>{p.text}</span>
              </li>
            ))}
          </ul>

          {vorschau && vorschau.fertig && (
            <p className="muted text-sm">
              Start {datumFormat.format(vorschau.start!)} · fertig voraussichtlich gegen{' '}
              {datumFormat.format(vorschau.fertig)}
            </p>
          )}

          <p className="muted text-xs">
            SPF, DKIM und DMARC prüft diese Seite nicht — das steht im Zustellbarkeits-Center,
            weil es an der Domain hängt und nicht an der Kampagne.
          </p>

          <div className="dialog__actions">
            <Button variant="secondary" onClick={onAbbrechen}>
              Abbrechen
            </Button>
            <Button onClick={onSenden} disabled={blockiert} loading={laeuft}>
              {empfaenger
                ? `${empfaenger.toLocaleString('de-DE')} E-Mails senden`
                : 'Senden'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Hochrechnung mit derselben Funktion, nach der auch der Worker freigibt. */
function rechne(profil: SendingPolicy, empfaenger: number) {
  const regeln: PolicyRegeln = {
    emailsPerHour: profil.emailsPerHour,
    emailsPerMinute: profil.emailsPerMinute,
    emailsPerDay: profil.emailsPerDay,
    warmupEnabled: profil.warmupEnabled,
    warmupStart: profil.warmupStart,
    warmupMultiplier: profil.warmupMultiplier,
    warmupCap: profil.warmupCap,
    sendWindows: profil.sendWindows,
    blackoutDates: profil.blackoutDates,
    contactCooldownMinutes: profil.contactCooldownMinutes,
    companyCooldownMinutes: profil.companyCooldownMinutes,
    maxDomainPerHour: profil.maxDomainPerHour,
    timezone: profil.timezone,
  };
  return versandVorschau(regeln, empfaenger);
}
